import { AnimatedCollapse, Button, ChevronUpIcon, cn, Heading, Label } from '@quickmd-llc/design-system'
import { PageLayout } from 'components/page-layouts/PageLayout'
import { PROVIDER_EVENT_NAMES, tracking } from 'lib/segment/tracking'
import { type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useSelector } from 'store/hooks/useSelector'
import { selectProviderId } from 'store/waitingRoom/WaitingRoomSelectors'
import {
  type HomeDeliveryGuideSectionId,
  parseHomeDeliveryGuideEntrySource,
  parseHomeDeliveryGuideSection,
} from './homeDeliveryGuideLink'
import { HOME_DELIVERY_GUIDE_SECTIONS } from './homeDeliveryGuideSections'
import { HOME_DELIVERY_GUIDE_SECTION_ANALYTICS } from './homeDeliveryTracking'

type ItemProps = {
  heading: string
  isOpen: boolean
  onToggle: () => void
  children: ReactNode
}

// Mirrors DS `CollapsibleCard`'s header (title, then a tertiary icon toggle carrying `aria-expanded`),
// but as a row inside one shared card: Figma draws the guide as a single card with 1px dividers, not a
// stack of separate cards.
const GuideItem = ({ heading, isOpen, onToggle, children }: ItemProps) => {
  const bodyId = useId()

  // A plain wrapper, not a labelled <section>: ten of those would be ten region landmarks.
  return (
    <div className='flex flex-col bg-white'>
      <div className='flex items-center justify-between gap-sp-2 p-sp-2'>
        <h2>
          <Label size='lg' className='text-text-dark'>
            {heading}
          </Label>
        </h2>
        <Button
          priority='tertiary'
          size='s'
          isIcon
          className='shrink-0'
          aria-expanded={isOpen}
          aria-controls={bodyId}
          aria-label={isOpen ? `Collapse ${heading}` : `Expand ${heading}`}
          onClick={onToggle}>
          <ChevronUpIcon iconSize='s' className={cn('transition-transform', !isOpen && 'rotate-180')} />
        </Button>
      </div>
      <AnimatedCollapse isOpen={isOpen} id={bodyId}>
        <div className='flex flex-col gap-sp-1 px-sp-2 pb-sp-2'>{children}</div>
      </AnimatedCollapse>
    </div>
  )
}

/**
 * QMD-4919: "How Home Delivery works", the provider's guide to prescribing for Home Delivery.
 *
 * A page of its own rather than a panel in the visit popup: the visit links here in a new tab, so a
 * provider can keep it open beside the call. It sits in the app's `PageLayout`, navigation and all,
 * like every other signed-in page: a page title over the content, with no Close. `?section=` picks the item that starts open — the first
 * by default, "How to change…" from the pickup warning.
 */
export const HomeDeliveryGuidePage = () => {
  const [searchParams] = useSearchParams()
  const [openSections, setOpenSections] = useState<ReadonlySet<HomeDeliveryGuideSectionId>>(
    () => new Set([parseHomeDeliveryGuideSection(searchParams)]),
  )
  const entrySource = parseHomeDeliveryGuideEntrySource(searchParams)
  const providerId = useSelector(selectProviderId)

  // QMD-4967: one view per visit to the page, sent once the provider profile is there to carry.
  const hasTrackedViewRef = useRef(false)
  useEffect(() => {
    if (!providerId || hasTrackedViewRef.current) return
    hasTrackedViewRef.current = true
    tracking.trackEvent(PROVIDER_EVENT_NAMES.VIEWED_HOW_HOME_DELIVERY_WORKS, {
      EntrySource: entrySource,
      ProviderId: providerId,
    })
  }, [providerId, entrySource])

  const toggle = (id: HomeDeliveryGuideSectionId) => {
    // Expanding only, every time. The section the page opens with is not an expand.
    if (!openSections.has(id) && providerId) {
      tracking.trackEvent(PROVIDER_EVENT_NAMES.HOME_DELIVERY_GUIDE_SECTION_EXPANDED, {
        ...HOME_DELIVERY_GUIDE_SECTION_ANALYTICS[id],
        EntrySource: entrySource,
        ProviderId: providerId,
      })
    }
    setOpenSections((previous) => {
      const next = new Set(previous)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // The app scopes Tailwind under `.tailwind-scoped` as descendant selectors (`src/index.css`), and
  // `PageLayout` opens no scope for its content, so the page opens it itself, one level above <main>.
  // Background and padding come from `PageLayout`; the title and the card below it follow the
  // Medications page, and the card fills the width, as other pages do.
  return (
    <PageLayout>
      <div className='tailwind-scoped'>
        <main className='flex flex-col gap-sp-3'>
          <Heading level='h1'>How Home Delivery works</Heading>
          <div className='flex flex-col gap-px overflow-clip rounded-lg border border-border-medium bg-border-medium'>
            {HOME_DELIVERY_GUIDE_SECTIONS.map((section) => (
              <GuideItem
                key={section.id}
                heading={section.heading}
                isOpen={openSections.has(section.id)}
                onToggle={() => toggle(section.id)}>
                {section.body}
              </GuideItem>
            ))}
          </div>
        </main>
      </div>
    </PageLayout>
  )
}
